/* global __GM_BUILD_VERSION__ */

import { createApp } from '@/lib/createApp';
import CollectionFilters from '@/CollectionFilters.vue';
import CollectionProducts from '@/CollectionProducts.vue';
import CollectionSort from '@/CollectionSort.vue';
import CollectionGridSize from '@/components/CollectionGridSize.vue';
import CollectionFilterButtons from '@/CollectionFilterButtons.vue';
import FsSearchField from '@/components/FsSearchField.vue';
import ProductBadgePDP from '@/ProductBadgePDP.vue';
import ColorVariantOption from '@/ColorVariantOption.vue';
import SizeVariantOption from '@/SizeVariantOption.vue';
import WidthVariantOption from '@/WidthVariantOption.vue';
import PDPaddToCart from '@/PDPaddToCart.vue';
import PDPaddToCartSticky from '@/PDPaddToCartSticky.vue';
import PDPinventory from '@/PDPinventory.vue';
import PDPPriceBlock from '@/PDPPriceBlock.vue';
import LiveSearch from '@/LiveSearch.vue';
import EndlessScroll from '@/EndlessScroll.vue';
import CollectionSeeMore from '@/CollectionSeeMore.vue';
import ProductRecommendations from '@/ProductRecommendations.vue';
// import CartRecommendations from '@/CartRecommendations.vue';
import PDPImages from '@/PDPImages.vue';
import mitt from 'mitt';
import CompleteTheLook from '@/CompleteTheLook.vue';
import PDPZoom from '@/PDPZoom.vue';
import PDPYotpoUGC from '@/PDPYotpoUGC.vue';
import PDPSectionDoubleRefresh from '@/PDPSectionDoubleRefresh.vue';
import ProductGridItemBadges from '@/ProductGridItemBadges.vue';
import CollectionSlider from '@/CollectionSlider.vue';
import CollectionSplitGrid from '@/CollectionSplitGrid.vue';
import ProductTileGroup from '@/ProductTileGroup.vue';
import ProductFamilyHelper from '@/components/ProductFamilyHelper.vue';
import PDPSetReminderDisplayLogic from '@/PDPSetReminderDisplayLogic.vue';
import ProductUpsellOptions from '@/components/ProductUpsellOptions.vue';
import OutfitPicker from '@/OutfitPicker.vue';
import CartWidget from '@/CartWidget.vue';
import { initPlytixDataLoader } from '@/lib/plytixDataLoader.js';
import { initCartFavorites } from '@/lib/cartFavorites.js';
import { attachCustomerAccountCookieOAuthToWindow } from '@/lib/customerAccountCookieOAuth.js';
import ProductComparison from '@/ProductComparison.vue';
import { bootstrap as bootstrapCustomerAuth, isConfiguredValue } from '@/lib/customerAuth';
import { fetchFirstCompanyLocationId } from '@/lib/customerAccount';
import { setBuyer, markBuyerSettled } from '@/lib/buyerContext';
import { setUniformSwatchWidths } from '@/lib/swatchWidths.js';
import AdaValidator from '@/AdaValidator.vue';
import { createVueComponentInitializer } from '@/lib/vueComponentRegistry.js';
export const emitter = mitt();

(async () => {
  const cap = window.customerAccount;
  const authConfigured = isConfiguredValue(cap?.clientId) && isConfiguredValue(cap?.tokenEndpoint) && isConfiguredValue(cap?.authorizationEndpoint);
  const authEnabled = window.customerAccountEnabled && window.isLoggedIn && authConfigured;
  if (!authEnabled) {
    markBuyerSettled();
    return;
  }
  try {
    const token = await bootstrapCustomerAuth();
    if (!token) {
      markBuyerSettled();
      return;
    }
    if (!window.b2bContextEnabled || !window.isB2B) {
      markBuyerSettled();
      return;
    }
    let companyLocationId = cap.companyLocationId || null;
    if (!companyLocationId) {
      companyLocationId = await fetchFirstCompanyLocationId();
    }
    setBuyer({
      customerAccessToken: token.access_token,
      ...(companyLocationId ? { companyLocationId } : {})
    });
  } catch (err) {
    void err;
    markBuyerSettled();
  }
})();

if (window.setClientWidthClass && !window.siteGlobals?.device) window.setClientWidthClass();

document.addEventListener('kameleoon:sdk:ready', event => {
  console.log('Vue heard kameleoon:sdk:ready event.', event.detail || {});
});

// Sources: startup → initial; start-full-pdp → fullPdp; theme:quickview:loaded → quickview;
// theme:recent-products:added → recentProducts; shopify:section:load → sectionLoad.
const COMPONENT_TRIGGERS = Object.freeze({
  initial: 'initial',
  fullPdp: 'full-pdp',
  quickview: 'quickview',
  recentProducts: 'recent-products',
  sectionLoad: 'section-load'
});

const isPdp = document.body.classList.contains('template-product');
let productTileObserver = null;
let trackVueComponent = () => {};

const findComponentRoot = (roots, selector) => roots.find(element => element.matches(selector));

const resolveUseSeeMore = roots => {
  const root = document.documentElement;
  if (root.classList.contains('kam-plp-see-more')) return true;
  if (root.classList.contains('kam-plp-endless-scroll')) return false;

  const kamJsOverride = window.theme?.kameleoonSeeMorePlpEnabled;
  if (kamJsOverride !== undefined) return kamJsOverride;

  return findComponentRoot(roots, '#fs-collection-see-more')?.getAttribute('data-default-enabled') === 'true';
};

function mountProductTileGroup(element) {
  if (!element || element.__vue_app__ || element.dataset.ptgMounted === 'true') return false;

  productTileObserver?.unobserve(element);
  element.dataset.ptgMounted = 'true';
  delete element.dataset.ptgObserved;
  try {
    createApp(ProductTileGroup).mount(element);
  } catch (error) {
    delete element.dataset.ptgMounted;
    throw error;
  }
  trackVueComponent(element);
  return true;
}

const unobserveProductTileGroups = root => {
  if (!productTileObserver || !root?.querySelectorAll) return;
  root.querySelectorAll('.product-tile-group[data-ptg-observed="true"]').forEach(element => {
    productTileObserver.unobserve(element);
    delete element.dataset.ptgObserved;
  });
};

const observeProductTileGroup = element => {
  if (!element || element.__vue_app__ || element.dataset.ptgMounted === 'true' || element.dataset.ptgObserved === 'true') return false;
  if (typeof IntersectionObserver === 'undefined') return mountProductTileGroup(element);

  if (!productTileObserver) {
    productTileObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          observer.unobserve(entry.target);
          mountProductTileGroup(entry.target);
        });
      },
      { rootMargin: '300px' }
    );
  }

  element.dataset.ptgObserved = 'true';
  productTileObserver.observe(element);
  return true;
};

window.mountProductTileGroup = mountProductTileGroup;

const componentRegistry = [
  {
    key: 'productUpsellOptions',
    selector: '#product-upsell-options',
    component: ProductUpsellOptions,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpSectionDoubleRefresh',
    selector: '.pdp-section-refresh',
    component: PDPSectionDoubleRefresh,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpReminder',
    selector: '#PDPSetReminderDisplayLogic',
    component: PDPSetReminderDisplayLogic,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'optionReminder',
    selector: '[id^="OpSetReminderDisplayLogic"]',
    component: PDPSetReminderDisplayLogic,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'productFamilyHelper',
    selector: '#ProductFamilyHelper',
    component: ProductFamilyHelper,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'helpGuideSize',
    selector: '#HelpGuideSize',
    component: ProductFamilyHelper,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'stickyAddToCart',
    selector: '#sticky-PDPaddtocart',
    component: PDPaddToCartSticky,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpYotpoUgc',
    selector: '#PDPYotpoUGC',
    component: PDPYotpoUGC,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpZoom',
    selector: '#vue-zoom',
    component: PDPZoom,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpImages',
    selector: '#PDPImages',
    component: PDPImages,
    triggers: [COMPONENT_TRIGGERS.fullPdp],
    beforeMount: element => {
      element.style.minHeight = `${element.offsetHeight}px`;
    },
    getProps: (element, context) => ({ images: context.images, media: context.media, pid: context.pid })
  },
  {
    key: 'completeTheLook',
    selector: '#CompleteTheLook',
    component: CompleteTheLook,
    triggers: [COMPONENT_TRIGGERS.fullPdp],
    getProps: (element, context) => ({ handle: context.handle, tags: context.tags, pid: context.pid })
  },
  {
    key: 'productRecommendations',
    selector: '#pdp-recommendations',
    component: ProductRecommendations,
    triggers: [COMPONENT_TRIGGERS.fullPdp],
    getProps: (element, context) => ({ pid: context.pid })
  },
  {
    key: 'cartWidget',
    selector: '#cart-recommendations',
    component: CartWidget,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'liveSearchDesktop',
    selector: '#live-search-fs',
    component: LiveSearch,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'liveSearchMobile',
    selector: '#live-search-fs-mobile',
    component: LiveSearch,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  /* Kameleoon See More PLP experiment: remove both entries and the resolver together when the
     experiment ends, and restore collection-fs.liquid to render only #fs-collection-endlessScroll
     (the see-more div is experiment-only). */
  {
    key: 'collectionSeeMore',
    selector: '#fs-collection-see-more',
    component: CollectionSeeMore,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad],
    shouldMount: (element, context) => resolveUseSeeMore(context.roots)
  },
  {
    key: 'endlessScroll',
    selector: '#fs-collection-endlessScroll',
    component: EndlessScroll,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad],
    shouldMount: (element, context) => !resolveUseSeeMore(context.roots),
    getProps: element => {
      const triggerDistance = element.getAttribute('data-trigger-distance');
      const loadingImage = element.getAttribute('data-loading-image');
      return { triggerDistance: triggerDistance ? parseInt(triggerDistance) : 500, loadingImage: loadingImage || null };
    }
  },
  {
    key: 'collectionFilters',
    selector: '#fs-collection-filters',
    component: CollectionFilters,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'searchField',
    selector: '#fs-search-field',
    component: FsSearchField,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'collectionFilterButtons',
    selector: '#fs-collection-filter-buttons',
    component: CollectionFilterButtons,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpBadgeRouter',
    selector: '.pdp-badge-router',
    component: ProductBadgePDP,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'productGridBadgeRouter',
    selector: '.product-item-badge-router',
    component: ProductGridItemBadges,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.recentProducts, COMPONENT_TRIGGERS.sectionLoad],
    shouldMount: (element, context) => context.trigger === COMPONENT_TRIGGERS.recentProducts || !isPdp
  },
  {
    key: 'collectionProducts',
    selector: '#fs-collection-products',
    component: CollectionProducts,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'collectionSort',
    selector: '#fs-collection-sorting',
    component: CollectionSort,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'collectionGridSize',
    selector: '#collection-grid-size',
    component: CollectionGridSize,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'colorVariantOptions',
    selector: '.color-variant-options',
    component: ColorVariantOption,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'sizeVariantOptions',
    selector: '.size-variant-options',
    component: SizeVariantOption,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'widthVariantOptions',
    selector: '.width-variant-options',
    component: WidthVariantOption,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'outfitPicker',
    selector: '#OutfitPicker',
    component: OutfitPicker,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpAddToCart',
    selector: '.PDPaddToCart',
    component: PDPaddToCart,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.quickview, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'quickviewReminder',
    selector: '#QuickviewSetReminderDisplayLogic',
    component: PDPSetReminderDisplayLogic,
    triggers: [COMPONENT_TRIGGERS.quickview]
  },
  {
    key: 'pdpInventory',
    selector: '.PDPinventory',
    component: PDPinventory,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'pdpPriceBlock',
    selector: '.PDPPriceBlock',
    component: PDPPriceBlock,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'collectionSlider',
    selector: '.fs-collection-slider',
    component: CollectionSlider,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'collectionSplitGrid',
    selector: '.fs-collection-split',
    component: CollectionSplitGrid,
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'productTileGroup',
    selector: '.product-tile-group',
    multiple: true,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad],
    mount: (element, context) => (context.trigger === COMPONENT_TRIGGERS.sectionLoad ? mountProductTileGroup(element) : observeProductTileGroup(element))
  },
  {
    key: 'productComparison',
    selector: '#product-comparison',
    component: ProductComparison,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  },
  {
    key: 'adaValidator',
    selector: '#ada-validator-root',
    component: AdaValidator,
    triggers: [COMPONENT_TRIGGERS.initial, COMPONENT_TRIGGERS.sectionLoad]
  }
];

const vueComponentInitializer = createVueComponentInitializer({ createApp, registry: componentRegistry });
const initializeVueComponents = vueComponentInitializer.initialize;
const teardownVueComponents = vueComponentInitializer.teardown;
trackVueComponent = vueComponentInitializer.track;

emitter.on('variant-color-selected', e => {
  const link = document.querySelector('.product__compare-plp-link');
  if (!link || !e.pid) return;
  const baseUrl = link.dataset.compareCollectionUrl;
  if (!baseUrl) return;
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('compare_pid', e.pid);
  if (e.color) url.searchParams.set('compare_color', e.color);
  link.href = url.toString();
});

let fullPdpContext = null;
const handleStartFullPdp = e => {
  fullPdpContext = e;
  initializeVueComponents(document, COMPONENT_TRIGGERS.fullPdp, e);
  emitter.off('start-full-pdp', handleStartFullPdp);
};
emitter.on('start-full-pdp', handleStartFullPdp);

if (isPdp) {
  window.addEventListener('theme:recent-products:added', () => {
    initializeVueComponents(document, COMPONENT_TRIGGERS.recentProducts);
  });
}

initPlytixDataLoader(emitter);

const cartFavoritesDrawer = document.querySelector('[data-cart-favorites]');
if (cartFavoritesDrawer) {
  initCartFavorites(cartFavoritesDrawer);
}

document.addEventListener('theme:quickview:loaded', () => {
  initializeVueComponents(document, COMPONENT_TRIGGERS.quickview);
});

// The quickview renders the Liquid variant options rather than the PDP's Vue islands, so it needs
// its own call into the same helper the PDP size and width options use. Only runs against the open
// drawer: measuring a closed one returns zero widths because of its content-visibility.
function alignQuickviewSwatchWidths(event) {
  const modal = event?.detail?.modal || document.querySelector('[data-quickview-modal].is-open');
  if (!modal) return;
  const excludeSwatchWidth = modal.querySelector('[data-quickview-head]')?.dataset.excludeSwatchWidth === 'true';
  ['2', '3'].forEach(position => {
    setUniformSwatchWidths(modal.querySelectorAll(`.selector-wrapper[data-option-position="${position}"] .radio__inputs .radio__button label`), excludeSwatchWidth);
  });
}

document.addEventListener('theme:quickview:opened', alignQuickviewSwatchWidths);
document.addEventListener('theme:resize:width', alignQuickviewSwatchWidths);

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', e => {
    initializeVueComponents(e.target, COMPONENT_TRIGGERS.sectionLoad);
    // start-full-pdp is one-shot, so reloaded sections reuse its first payload.
    if (fullPdpContext) initializeVueComponents(e.target, COMPONENT_TRIGGERS.fullPdp, fullPdpContext);
  });
  document.addEventListener('shopify:section:unload', e => {
    unobserveProductTileGroups(e.target);
    teardownVueComponents(e.target);
  });
}

initializeVueComponents(document, COMPONENT_TRIGGERS.initial);

attachCustomerAccountCookieOAuthToWindow();

console.log(`gm.js v${__GM_BUILD_VERSION__} loaded`);
